import { useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import useTranslation from 'next-translate/useTranslation';

// Components
import Icon from '@/components/base/Icon';

const navigationClassName =
    'flex items-center text-icoGray-500 dark:text-icoDarkMode-wolf text-sm disabled:text-icoGray-500/50 disabled:dark:text-icoDarkMode-wolf/50 disabled:cursor-not-allowed';

const TablePagination = ({ reports, isLoading, errorReports, requestParams, setRequestParams }) => {
    const { t } = useTranslation();
    const currentPage = requestParams?.page ?? 1;
    const nbPages = reports?.last_page;

    const goToPage = (page) => {
        setRequestParams((prevState) => ({
            ...prevState,
            page
        }));
    };

    const getVisiblePages = useCallback(() => {
        if (nbPages < 6) {
            return [...Array(nbPages).keys()].map((index) => index + 1);
        } else {
            if (currentPage % 5 >= 0 && currentPage > 2 && currentPage < nbPages - 1) {
                return [
                    currentPage - 2,
                    currentPage - 1,
                    currentPage,
                    currentPage + 1,
                    currentPage + 2
                ];
            } else if (currentPage % 5 >= 0 && currentPage > 2 && currentPage >= nbPages - 1) {
                return [nbPages - 4, nbPages - 3, nbPages - 2, nbPages - 1, nbPages];
            } else {
                return [1, 2, 3, 4, 5];
            }
        }
    }, [currentPage, nbPages]);

    useEffect(() => {
        // change to last new last page if previous last page is deleted
        if (currentPage > reports?.last_page) {
            goToPage(reports?.last_page);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [reports?.last_page]);

    if (isLoading) {
        return (
            <div className="mx-auto mt-5 h-3.5 max-w-[200px] animate-pulse bg-icoGray-100 dark:bg-icoDarkMode-deepBlack" />
        );
    }

    if (errorReports || (!reports?.data?.length && currentPage === 1)) {
        return null;
    }

    const canPreviousPage = currentPage !== 1;
    const canNextPage = currentPage !== reports?.last_page;

    return (
        <div className="mt-5 flex w-full items-center justify-center" aria-label="pagination">
            <button
                className={navigationClassName}
                onClick={() => goToPage(1)}
                disabled={!canPreviousPage}>
                <Icon
                    icon="double-chevron-left"
                    className={clsx('text-sm text-icoGray-500 dark:text-icoDarkMode-wolf', {
                        'cursor-not-allowed text-icoGray-500/50 dark:text-icoDarkMode-wolf/50':
                            !canPreviousPage
                    })}
                />
            </button>
            <button
                className={clsx(navigationClassName, 'ml-2')}
                onClick={() => goToPage(currentPage - 1)}
                disabled={!canPreviousPage}>
                <Icon icon="chevron-left" />
            </button>

            <div className="mx-1 flex items-center gap-x-1">
                {getVisiblePages()?.map((pageNumber) => (
                    <button
                        key={pageNumber}
                        className={clsx('flex h-3.5 w-3.5 items-center justify-center text-sm', {
                            'font-GSemiBold text-icoGray-500 dark:text-icoDarkMode-wolf':
                                currentPage !== pageNumber,
                            'font-GBold text-icoBlue': currentPage === pageNumber
                        })}
                        aria-label={
                            currentPage === pageNumber ? t('commons:current_page') : pageNumber
                        }
                        onClick={() => goToPage(pageNumber)}>
                        {pageNumber}
                    </button>
                ))}
            </div>

            <button
                onClick={() => goToPage(currentPage + 1)}
                disabled={!canNextPage}
                className={clsx(navigationClassName, 'mr-2')}>
                <Icon icon="chevron-right" />
            </button>
            <button
                className={navigationClassName}
                onClick={() => goToPage(reports?.last_page)}
                disabled={!canNextPage}>
                <Icon icon="double-chevron-right" />
            </button>
        </div>
    );
};

TablePagination.propTypes = {
    requestParams: PropTypes.object,
    setRequestParams: PropTypes.func
};

export default TablePagination;
